import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../api';
import './DashboardAdmin.css';

function DashboardAdmin({ maintenanceMode, setMaintenanceMode, handleLogout }) {
    const navigate = useNavigate();

    const onLogoutClick = () => {
        handleLogout();
        navigate('/login');
    };

    // ── Enquiries summary ─────────────────────────────────────────────────────
    const [enqTotal, setEnqTotal] = useState(null);
    const [enqUnreplied, setEnqUnreplied] = useState(null);
    const [enqLoading, setEnqLoading] = useState(true);

    // ── Orphan checker ────────────────────────────────────────────────────────
    const [orphans, setOrphans] = useState([]);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // ── Maintenance toggle ────────────────────────────────────────────────────
    const [maintLoading, setMaintLoading] = useState(false);
    const [maintError, setMaintError] = useState('');

    useEffect(() => {
        fetchEnqSummary();
    }, []);

    const fetchEnqSummary = async () => {
        setEnqLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/enquiries`);
            setEnqTotal(res.data.length);
            setEnqUnreplied(res.data.filter((e) => !e.replied).length);
        } catch (err) {
            console.error('Failed to load enquiry summary:', err);
        } finally {
            setEnqLoading(false);
        }
    };

    const runCheck = async () => {
        setLoading(true);
        setError('');
        setOrphans([]);
        setTotal(null);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/orphan-images`);
            setOrphans(res.data.orphans);
            setTotal(res.data.total);
        } catch (err) {
            setError('Failed to run orphan check. See console for details.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleMaintenance = async () => {
        setMaintLoading(true);
        setMaintError('');
        try {
            const res = await axios.post(`${API_BASE_URL}/api/admin/maintenance`, {
                enabled: !maintenanceMode,
            });
            setMaintenanceMode(res.data.enabled);
        } catch (err) {
            setMaintError('Failed to update maintenance mode.');
            console.error(err);
        } finally {
            setMaintLoading(false);
        }
    };

    const formatSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatDate = (dateStr) => new Date(dateStr).toLocaleString('en-GB');

    return (
        <div className="dashboard">
            <div className="dashboard__header">
                <div>
                    <h1 className="dashboard__title">Admin Dashboard</h1>
                    <p className="dashboard__subtitle">Welcome back. Here's an overview of your site.</p>
                </div>
            </div>

            {/* ── SUMMARY CARDS GRID ─────────────────────────────────────────── */}
            <div className="dashboard__grid">

                <Link to="/admin/products">
                    <div className="summary-card">
                        <div className="summary-card__icon summary-card__icon--products">
                            <i className='bx bx-shopping-bag'></i>
                        </div>
                        <div className="summary-card__body">
                            <div className="summary-card__label">Products</div>
                            <div className="summary-card__desc">Manage your product catalogue</div>
                        </div>
                        <span className="summary-card__arrow">→</span>
                    </div>
                </Link>

                <Link to="/admin/categories">
                    <div className="summary-card">
                        <div className="summary-card__icon summary-card__icon--categories">
                            <i className='bx bx-category'></i>
                        </div>
                        <div className="summary-card__body">
                            <div className="summary-card__label">Categories</div>
                            <div className="summary-card__desc">Manage product categories</div>
                        </div>
                        <span className="summary-card__arrow">→</span>
                    </div>
                </Link>

                <Link to="/admin/enquiries">
                    <div className="summary-card">
                        <div className="summary-card__icon summary-card__icon--enquiries">
                            <i className='bx bx-envelope'></i>
                        </div>
                        <div className="summary-card__body">
                            <div className="summary-card__label">
                                Enquiries
                                {!enqLoading && enqUnreplied > 0 && (
                                    <span className="summary-card__badge">{enqUnreplied} unreplied</span>
                                )}
                            </div>
                            <div className="summary-card__desc">
                                {enqLoading ? 'Loading...' : `${enqTotal} total enquiries`}
                            </div>
                        </div>
                        <span className="summary-card__arrow">→</span>
                    </div>
                </Link>

                <Link to="/admin/users">
                    <div className="summary-card">
                        <div className="summary-card__icon summary-card__icon--users">
                            <i className='bx bx-user'></i>
                        </div>
                        <div className="summary-card__body">
                            <div className="summary-card__label">Users</div>
                            <div className="summary-card__desc">Manage admin users</div>
                        </div>
                        <span className="summary-card__arrow">→</span>
                    </div>
                </Link>

            </div>

            {/* ── ORPHAN IMAGE CHECKER ──────────────────────────────────────────── */}
            <div className="dash-card">
                <h2>🗂️ Orphan Image Checker</h2>
                <p className="dash-card__desc">
                    Scans your AWS S3 bucket and finds images that are no longer
                    referenced by any product or category in the database.
                </p>

                <button
                    onClick={runCheck}
                    disabled={loading}
                    className={`orphan-btn${loading ? ' orphan-btn--scanning' : ''}`}
                >
                    {loading ? 'Scanning...' : 'Run Orphan Check'}
                </button>

                {error && <p className="orphan-error">{error}</p>}

                {total !== null && !loading && (
                    <p className={`orphan-result ${total === 0 ? 'orphan-result--clean' : 'orphan-result--dirty'}`}>
                        {total === 0
                            ? '✅ No orphaned images found — your S3 bucket is clean!'
                            : `⚠️ ${total} orphaned image${total !== 1 ? 's' : ''} found`}
                    </p>
                )}

                {orphans.length > 0 && (
                    <div className="orphan-table-wrap">
                        <table className="orphan-table">
                            <thead>
                            <tr>
                                <th>Preview</th>
                                <th>Filename</th>
                                <th>Size</th>
                                <th>Last Modified</th>
                                <th>S3 URL</th>
                            </tr>
                            </thead>
                            <tbody>
                            {orphans.map((obj, i) => (
                                <tr key={i}>
                                    <td>
                                        <img
                                            src={obj.url}
                                            alt="orphan"
                                            className="orphan-table__thumb"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </td>
                                    <td>{obj.key}</td>
                                    <td>{formatSize(obj.size)}</td>
                                    <td>{formatDate(obj.lastModified)}</td>
                                    <td>
                                        <a href={obj.url} target="_blank" rel="noopener noreferrer" className="orphan-table__link">
                                            View
                                        </a>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── MAINTENANCE MODE ──────────────────────────────────────────────── */}
            <div className="dash-card">
                <div className="maint__header">
                    <div>
                        <h2>🔧 Maintenance Mode</h2>
                        <p className="dash-card__desc">
                            When enabled, the public site shows a maintenance page.
                            Logged-in admins can still browse the full site.
                        </p>
                    </div>
                    <div className={`maint__status ${maintenanceMode ? 'maint__status--on' : 'maint__status--off'}`}>
                        {maintenanceMode ? '● LIVE — Maintenance ON' : '● Site is LIVE'}
                    </div>
                </div>

                <div className="maint__toggle-row">
                    <button
                        onClick={toggleMaintenance}
                        disabled={maintLoading}
                        className={`maint__btn ${maintenanceMode ? 'maint__btn--disable' : 'maint__btn--enable'}`}
                    >
                        {maintLoading
                            ? 'Updating...'
                            : maintenanceMode
                                ? '✕ Disable Maintenance Mode'
                                : '⚠ Enable Maintenance Mode'}
                    </button>

                    {maintenanceMode && (
                        <span className="maint__warning">
                            ⚠️ The public site is currently showing the maintenance page.
                        </span>
                    )}
                </div>

                {maintError && <p className="orphan-error">{maintError}</p>}
            </div>

            {/* ── LOGOUT ─────────────────────────────────────────────────────────── */}
            <div className="dashboard__logout-row">
                <button type="button" className="dashboard__logout-btn" onClick={onLogoutClick}>
                    <i className='bx bx-log-out'></i> Logout
                </button>
            </div>
        </div>
    );
}

export default DashboardAdmin;